import { useTranslation } from "react-i18next";

import { useCcGateHistory } from "@/hooks/useCodeCompanion";
import { tp } from "@/lib/plural";

/**
 * Quando o gate foi pedido e o que ele respondeu (S-064 onda 4).
 *
 * O gate de prontidão era calculado, mostrado, obedecido — e não guardado. Depois de
 * promover não havia como responder "com base em quê?", e não dá para reconstruir: o
 * veredito depende do estado de N objetos naquele instante, e qualquer deploy posterior
 * o muda.
 *
 * **A recusa é a metade que interessa.** Sucesso é o esperado; "tentamos promover às
 * 14:20 e faltavam dois objetos" é o que alguém vai querer entender depois, e não
 * existia em lugar nenhum.
 *
 * Uma linha por **pedido de transição**, não por leitura: o painel do gate faz polling,
 * e uma linha por poll afogaria as poucas que têm valor.
 */
export default function GateHistory({
  projectId,
  packageId,
}: {
  projectId: string;
  packageId: number;
}) {
  const { t } = useTranslation();
  const { data, isLoading } = useCcGateHistory(projectId, packageId);

  if (isLoading) return null;

  // Pacote que nunca teve promoção pedida não tem história — e dizer isso é melhor que
  // uma caixa vazia, que parece falha de carregamento.
  if (!data?.items.length) {
    return (
      <div className="rounded-lg border border-dashed px-3 py-2 text-[11px] text-muted-foreground">
        {t("Promotion has never been requested for this package.")}
      </div>
    );
  }

  return (
    <div
      data-testid="gate-history"
      className="overflow-hidden rounded-lg border border-border bg-card"
    >
      <div className="border-b border-border px-3 py-2 text-[10px] font-bold uppercase tracking-wide text-muted-foreground">
        {t("Gate history")}
      </div>
      <ul className="divide-y divide-border">
        {data.items.map((ev) => (
          <li key={ev.id} className="px-3 py-2 text-[11px]">
            <div className="flex flex-wrap items-baseline gap-2">
              <span
                className="font-mono text-[10px] font-semibold uppercase"
                style={{ color: ev.ok ? "#2b8f57" : "#b02a31" }}
              >
                {ev.ok ? t("passed") : t("refused")}
              </span>
              <span className="font-mono text-[10px] text-muted-foreground">
                {tp(t, ev.objects.length, "1 object", "{{n}} objects")}
              </span>
              {ev.actor_type && (
                <span className="font-mono text-[10px] text-muted-foreground">
                  {ev.actor_type}
                </span>
              )}
              <div className="flex-1" />
              {ev.evaluated_at && (
                <span className="font-mono text-[10px] text-muted-foreground">
                  {new Date(ev.evaluated_at).toLocaleString()}
                </span>
              )}
            </div>
            {/* O que faltava, quando faltou: é o conteúdo da recusa, não um detalhe. */}
            {ev.missing.length > 0 && (
              <ul data-testid="gate-missing" className="mt-1 space-y-0.5">
                {ev.missing.map((m, i) => (
                  <li key={i} className="break-words text-muted-foreground">
                    {m}
                  </li>
                ))}
              </ul>
            )}
            {/* Aviso não bloqueia, e é justamente por isso que ele precisa aparecer
                inclusive no veredito aprovado: promover sem prova de comportamento é
                decisão legítima, e esconder isso faz o dossiê parecer mais forte. */}
            {(ev.warnings ?? []).length > 0 && (
              <ul data-testid="gate-warnings" className="mt-1 space-y-0.5">
                {ev.warnings.map((w, i) => (
                  <li key={i} className="break-words" style={{ color: "#8a4b00" }}>
                    {w}
                  </li>
                ))}
              </ul>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
